/**
 * Pairs Well With Bundle — assets/pairs-well-bundle.js
 *
 * Integrates with Streamline's native AJAX cart flow:
 *   1. Add both variants via /cart/add.js
 *   2. Apply the bundle discount via /cart/update.js (Shopify Cart AJAX API)
 *   3. Dispatch `ajaxProduct:added` on document so CartDrawer rebuilds and opens
 *
 * Discount notes:
 *   - POST /cart/update.js { discount: 'CODE' } replaces the cart's code list.
 *   - Multiple bundles on one PDP (A+B and A+C) must re-apply every still-valid
 *     code as a comma-separated list, or the first bundle discount is dropped.
 *   - /cart/add.js can also clear previously applied codes; snapshot first.
 */

(function () {
  'use strict';

  if (window.__pwbBundleInit) return;
  window.__pwbBundleInit = true;

  const SECTION_SELECTOR = '[data-section-type="pairs-well-bundle"]';
  const ADD_BTN_SELECTOR = '[data-pwb-add-bundle]';
  const ERROR_SELECTOR = '[data-pwb-error]';

  function getCartAddUrl() {
    if (window.theme && window.theme.routes && window.theme.routes.cartAdd) {
      return window.theme.routes.cartAdd;
    }
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cart/add.js';
    }
    return '/cart/add.js';
  }

  function getCartUpdateUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + 'cart/update.js';
    }
    return '/cart/update.js';
  }

  const AJAX_HEADERS = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  const STORAGE_KEY = 'ck_active_discount_code';
  const STORAGE_KEY_LIST = 'ck_active_discount_codes';

  function uniqueCodes(codes) {
    const seen = {};
    const out = [];
    (codes || []).forEach(function (code) {
      const normalized = String(code || '').trim();
      if (!normalized) return;
      const key = normalized.toUpperCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push(normalized);
    });
    return out;
  }

  function readStoredCodes() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_LIST) || localStorage.getItem(STORAGE_KEY_LIST);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return uniqueCodes(parsed);
        } catch (e) {}
        return uniqueCodes(String(raw).split(','));
      }
      const legacy = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
      return uniqueCodes(legacy ? [legacy] : []);
    } catch (e) {
      return [];
    }
  }

  function writeStoredCodes(codes) {
    const list = uniqueCodes(codes);
    try {
      const payload = JSON.stringify(list.map(function (c) { return c.toUpperCase(); }));
      sessionStorage.setItem(STORAGE_KEY_LIST, payload);
      localStorage.setItem(STORAGE_KEY_LIST, payload);
      if (list.length) {
        sessionStorage.setItem(STORAGE_KEY, list[list.length - 1].toUpperCase());
        localStorage.setItem(STORAGE_KEY, list[list.length - 1].toUpperCase());
      }
    } catch (e) {}
  }

  async function fetchCart() {
    const res = await fetch('/cart.js?' + Date.now(), { credentials: 'same-origin' });
    return res.json();
  }

  function codesFromCart(cart) {
    const codes = [];
    ((cart && cart.discount_codes) || []).forEach(function (dc) {
      if (dc && dc.code) codes.push(dc.code);
    });
    ((cart && cart.cart_level_discount_applications) || []).forEach(function (app) {
      if (app && app.code) codes.push(app.code);
      else if (app && app.title) codes.push(app.title);
    });
    ((cart && cart.items) || []).forEach(function (item) {
      (item.discounts || []).forEach(function (discount) {
        if (discount && discount.title) codes.push(discount.title);
      });
      (item.discount_allocations || []).forEach(function (alloc) {
        const app = alloc && alloc.discount_application;
        if (app && app.code) codes.push(app.code);
        else if (app && app.title) codes.push(app.title);
      });
    });
    return uniqueCodes(codes);
  }

  function sectionNeededVariantIds(section) {
    const ids = [];
    const currentId = String(section.dataset.currentVariantId || '').trim();
    if (currentId) ids.push(currentId);
    String(section.dataset.bundleVariantIds || '')
      .split(',')
      .map(function (id) { return id.trim(); })
      .filter(Boolean)
      .forEach(function (id) { ids.push(id); });
    return ids;
  }

  function cartHasAllVariants(cart, variantIds) {
    const present = {};
    ((cart && cart.items) || []).forEach(function (item) {
      present[String(item.variant_id)] = true;
    });
    return variantIds.length > 0 && variantIds.every(function (id) { return !!present[id]; });
  }

  function codesFromBundlesInCart(cart) {
    const codes = [];
    document.querySelectorAll('[data-pwb-bundle]').forEach(function (bundle) {
      const code = (bundle.dataset.discountCode || '').trim();
      if (!code) return;
      const variantIds = sectionNeededVariantIds(bundle);
      if (variantIds.length && cartHasAllVariants(cart, variantIds)) {
        codes.push(code);
      }
    });
    return uniqueCodes(codes);
  }

  function collectDiscountCodes(cart, extraCode) {
    return uniqueCodes(
      codesFromCart(cart)
        .concat(readStoredCodes())
        .concat(codesFromBundlesInCart(cart))
        .concat(extraCode ? [extraCode] : [])
    );
  }

  async function addItemsToCart(items) {
    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: AJAX_HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || data.message || 'Could not add items to cart.');
    }

    return data;
  }

  function cartHasCode(cart, code) {
    const target = String(code || '').trim().toUpperCase();
    if (!target) return false;
    return codesFromCart(cart).some(function (existing) {
      return String(existing).toUpperCase() === target;
    });
  }

  async function postCartDiscount(value) {
    const response = await fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: AJAX_HEADERS,
      credentials: 'same-origin',
      body: JSON.stringify({ discount: value }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || data.message || 'Could not apply discount code.');
    }
    return data;
  }

  async function applyDiscountCodes(codes) {
    const list = uniqueCodes(codes);
    if (!list.length) return fetchCart();

    let cart = await postCartDiscount(list.join(','));

    const missing = list.filter(function (code) { return !cartHasCode(cart, code); });
    if (missing.length) {
      try {
        await fetch('/discount/' + list.map(encodeURIComponent).join(','), {
          method: 'GET',
          credentials: 'same-origin',
          redirect: 'manual',
        });
        cart = await fetchCart();
      } catch (e) {}
    }

    writeStoredCodes(list);
    return cart;
  }

  function cartHasDiscount(cart) {
    if (!cart) return false;

    if (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length) {
      return true;
    }

    return (cart.items || []).some(function (item) {
      return Array.isArray(item.discounts) && item.discounts.length > 0;
    });
  }

  /**
   * Trigger Streamline's native post-add-to-cart flow.
   * CartDrawer listens on document for `ajaxProduct:added`, then rebuilds and opens.
   */
  function triggerStreamlineCartAdded(addResponse, button) {
    document.body.classList.add('cart-has-items');

    document.dispatchEvent(
      new CustomEvent('ajaxProduct:added', {
        detail: {
          product: addResponse,
          addToCartBtn: button,
        },
      })
    );

    // Fallback for themes / cart types that only listen for cart:open on document
    if (window.theme && window.theme.settings && window.theme.settings.cartType !== 'drawer') {
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    }
  }

  function setLoading(btn, isLoading) {
    if (isLoading) {
      btn.setAttribute('data-loading', '');
      btn.setAttribute('disabled', '');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.removeAttribute('data-loading');
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-busy');
    }
  }

  function showError(el, message) {
    if (!el) return;
    el.textContent = message;
    el.removeAttribute('hidden');
  }

  function clearError(el) {
    if (!el) return;
    el.textContent = '';
    el.setAttribute('hidden', '');
  }

  async function handleBundleAdd(bundle, btn) {
    const errorEl = bundle.querySelector(ERROR_SELECTOR);
    const discountCode = bundle.dataset.discountCode;
    const currentVariantId = parseInt(bundle.dataset.currentVariantId, 10);
    const bundleVariantIds = (bundle.dataset.bundleVariantIds || '')
      .split(',')
      .map(function (id) { return parseInt(id.trim(), 10); })
      .filter(function (id) { return id > 0; });

    if (!currentVariantId || bundleVariantIds.length === 0) {
      showError(errorEl, 'Bundle is not configured correctly. Please contact support.');
      return;
    }

    clearError(errorEl);
    setLoading(btn, true);

    try {
      const items = [{ id: currentVariantId, quantity: 1 }];
      bundleVariantIds.forEach(function (variantId) {
        items.push({ id: variantId, quantity: 1 });
      });

      let snapshotCodes = [];
      try {
        snapshotCodes = collectDiscountCodes(await fetchCart(), '');
      } catch (e) {
        snapshotCodes = readStoredCodes();
      }

      const addResponse = await addItemsToCart(items);

      const cartAfterAdd = await fetchCart();
      const merged = uniqueCodes(
        snapshotCodes.concat(collectDiscountCodes(cartAfterAdd, discountCode))
      );
      if (merged.length) {
        try {
          await applyDiscountCodes(merged);
        } catch (discountErr) {
          console.warn('[PWB] Could not apply discount codes:', merged.join(','), discountErr);
        }
      }

      triggerStreamlineCartAdded(addResponse, btn);
    } catch (err) {
      console.error('[PWB] Bundle add-to-cart error:', err);
      showError(errorEl, err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(btn, false);
    }
  }

  function initSection(section) {
    section.querySelectorAll(ADD_BTN_SELECTOR).forEach(function (btn) {
      if (btn.dataset.pwbBound === 'true') return;
      btn.dataset.pwbBound = 'true';
      btn.addEventListener('click', function () {
        const bundle = btn.closest('[data-pwb-bundle]');
        if (!bundle) return;
        handleBundleAdd(bundle, btn);
      });
    });
  }

  function sectionWrapper(section) {
    return section.closest('.shopify-section') || section;
  }

  function isNextBundleSection(prevSection, nextSection) {
    const prev = sectionWrapper(prevSection);
    const next = sectionWrapper(nextSection);
    let node = prev.nextElementSibling;
    while (node && node !== next) {
      if (node.classList && node.classList.contains('shopify-section')) return false;
      node = node.nextElementSibling;
    }
    return node === next;
  }

  function groupBundleSections(sections) {
    const groups = [];
    let current = [];
    sections.forEach(function (section) {
      if (!current.length || isNextBundleSection(current[current.length - 1], section)) {
        current.push(section);
      } else {
        groups.push(current);
        current = [section];
      }
    });
    if (current.length) groups.push(current);
    return groups;
  }

  function setGroupOpen(group, isOpen) {
    group.forEach(function (section) {
      section.classList.toggle('is-pwb-open', isOpen);
      sectionWrapper(section).classList.toggle('is-pwb-group-open', isOpen);
    });
  }

  function bindSeeAll(link) {
    if (link.dataset.pwbSeeAllBound === 'true') return;
    link.dataset.pwbSeeAllBound = 'true';
    link.setAttribute('aria-expanded', 'false');
    link.addEventListener('click', function (event) {
      event.preventDefault();
      const section = link.closest(SECTION_SELECTOR);
      if (!section) return;
      const groups = groupBundleSections(Array.from(document.querySelectorAll(SECTION_SELECTOR)));
      const group = groups.filter(function (items) {
        return items.indexOf(section) !== -1;
      })[0] || [section];
      const willOpen = link.getAttribute('aria-expanded') !== 'true';
      setGroupOpen(group, willOpen);
      link.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
  }

  function initSeeAll() {
    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    sections.forEach(function (section) {
      section.classList.remove('is-pwb-open');
      sectionWrapper(section).classList.remove('is-pwb-group-open');
    });

    groupBundleSections(sections).forEach(function (group) {
      const bundles = [];
      const seeAlls = [];
      group.forEach(function (section, sectionIndex) {
        section.classList.toggle('is-pwb-follow', sectionIndex > 0);
        section.querySelectorAll('[data-pwb-bundle]').forEach(function (bundle) {
          bundles.push(bundle);
        });
        const link = section.querySelector('[data-pwb-see-all]');
        if (link) seeAlls.push(link);
      });
      bundles.forEach(function (bundle, bundleIndex) {
        bundle.classList.toggle('pwb-bundle--extra', bundleIndex > 0);
      });

      seeAlls.forEach(function (link, index) {
        const wrap = link.closest('.pwb_see_all');
        const show = bundles.length > 1 && index === seeAlls.length - 1;
        if (!wrap) return;
        link.setAttribute('aria-expanded', 'false');
        wrap.classList.toggle('is-visible', show);
        wrap.hidden = !show;
        if (show) bindSeeAll(link);
      });
    });
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
    initSeeAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
    initSeeAll();
  });

  document.addEventListener('shopify:section:unload', function () {
    setTimeout(initSeeAll, 0);
  });
})();
